import { useEffect, useMemo, useState } from 'react';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import {
  useRorosRoleContracts,
  useRorosRoleContractUpdate,
} from '@/hooks/roros/role-contracts';
import type { RorosRoleContractDTO } from '@/services/roros/governance.service';
import type { RorosActorDTO } from '@/types/roros/actors';
import { RorosActorType } from '@/types/roros/enums';

interface UseActorRoleContractParams {
  actor?: RorosActorDTO | null;
  isOpen: boolean;
}

/**
 * Owns the AI-operator "role contract" concern of UnifiedActorEditor: the
 * linked/assignable contract queries and derivations, picker state, the
 * open-sync effect, and the immediate-save select/unlink handlers.
 * Behaviour-identical extraction (M10f decomposition).
 */
export function useActorRoleContract({ actor, isOpen }: UseActorRoleContractParams) {
  const isAiOp = actor?.actorType === RorosActorType.AI_OPERATOR;

  const roleContractsQuery = useRorosRoleContracts({ actorId: actor?.id ?? '' });
  const allContractsQuery = useRorosRoleContracts();
  const roleContractUpdate = useRorosRoleContractUpdate();

  const linkedRoleContract = useMemo((): RorosRoleContractDTO | null => {
    const list = (roleContractsQuery.data as any)?.data ?? (roleContractsQuery.data as any) ?? [];
    if (!Array.isArray(list)) return null;
    return list.find((c: any) => c?.actorId === actor?.id || c?.actor_id === actor?.id) ?? null;
  }, [roleContractsQuery.data, actor?.id]);

  const isContractActive = linkedRoleContract?.status === 'active';
  // A linked contract is editable (can be unlinked/swapped) only if it is draft.
  const isLinkedContractEditable = linkedRoleContract?.status === 'draft';

  // Only draft contracts that are not already linked to a different actor can be assigned.
  const assignableContracts = useMemo((): RorosRoleContractDTO[] => {
    const list = (allContractsQuery.data as any)?.data ?? (allContractsQuery.data as any) ?? [];
    if (!Array.isArray(list)) return [];
    return (list as RorosRoleContractDTO[]).filter(
      (c) =>
        c.status === 'draft' &&
        (c.actorId === null || c.actorId === actor?.id),
    );
  }, [allContractsQuery.data, actor?.id]);

  const [pickerContractId, setPickerContractId] = useState('');
  const [contractAssigning, setContractAssigning] = useState(false);

  // Sync role contract picker from linkedRoleContract (AC-11 superseded — budget sync removed)
  useEffect(() => {
    if (!isOpen || !isAiOp) return;
    setPickerContractId(linkedRoleContract?.id ?? '');
  }, [isOpen, linkedRoleContract]);

  const handleContractSelect = async (newContractId: string) => {
    if (!actor?.id) return;
    if (newContractId === (linkedRoleContract?.id ?? '')) return;
    setPickerContractId(newContractId);
    setContractAssigning(true);
    try {
      if (linkedRoleContract && newContractId !== '' && isLinkedContractEditable) {
        // Swap: unlink existing draft contract first
        await roleContractUpdate.mutateAsync({ id: linkedRoleContract.id, payload: { actor_id: null } });
      }
      if (newContractId !== '') {
        await roleContractUpdate.mutateAsync({ id: newContractId, payload: { actor_id: actor.id } });
        showToast.success(t('Role Contract assigned'));
      }
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to assign Role Contract'));
      setPickerContractId(linkedRoleContract?.id ?? '');
    } finally {
      setContractAssigning(false);
    }
  };

  const handleContractUnlink = async () => {
    if (!linkedRoleContract || !actor?.id) return;
    if (!isLinkedContractEditable) return; // active contracts cannot be unlinked via edit
    setContractAssigning(true);
    try {
      await roleContractUpdate.mutateAsync({ id: linkedRoleContract.id, payload: { actor_id: null } });
      setPickerContractId('');
      showToast.success(t('Role Contract unlinked'));
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : t('Failed to remove Role Contract'));
    } finally {
      setContractAssigning(false);
    }
  };

  return {
    linkedRoleContract,
    isContractActive,
    isLinkedContractEditable,
    assignableContracts,
    pickerContractId,
    contractAssigning,
    handleContractSelect,
    handleContractUnlink,
  };
}
